import type { InvoiceDraft } from "@vaulted/shared";
import { INVOICE_FILE_TYPES, INVOICE_MAX_BYTES, INVOICE_MAX_FILES } from "@vaulted/shared";
import { useRef, useState } from "react";
import { errorMessage, post } from "../api";
import { Alert, cx } from "./ui";

/** Photos are re-encoded as JPEG so they stay small (the AI reads about 1,500px anyway). */
const MAX_SIDE = 2400;
const IMAGE_MAX_BYTES = 3_500_000;

async function prepare(file: File): Promise<File> {
  if (file.type === "application/pdf") {
    if (file.size > INVOICE_MAX_BYTES) throw new Error(`${file.name} is over 4.5 MB. Upload a screenshot of the invoice instead.`);
    return file;
  }
  if (!file.type.startsWith("image/") && !/\.(heic|heif)$/i.test(file.name)) throw new Error(`${file.name}: upload a PDF, screenshot, or photo.`);
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    if (file.type in INVOICE_FILE_TYPES && file.size <= IMAGE_MAX_BYTES) return file;
    throw new Error(`${file.name} couldn't be opened. Take a screenshot of it or save it as a JPG.`);
  }
  const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  if (scale === 1 && file.type in INVOICE_FILE_TYPES && file.size <= IMAGE_MAX_BYTES) {
    bitmap.close();
    return file;
  }
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const g = canvas.getContext("2d")!;
  g.fillStyle = "#fff";
  g.fillRect(0, 0, canvas.width, canvas.height);
  g.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  for (const q of [0.85, 0.7, 0.55]) {
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", q));
    if (blob && blob.size <= IMAGE_MAX_BYTES) return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  }
  throw new Error(`${file.name} is too large. Crop it to the invoice and try again.`);
}

/**
 * "Fill from invoice": drop, pick, or paste an invoice (PDF, screenshot, or photo; up to 5 files for one order).
 * Uploads each file, has the AI read them, and hands back a draft for the form. Nothing is saved here.
 */
export function InvoiceUpload({ onDraft }: { onDraft: (d: InvoiceDraft) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [over, setOver] = useState(false);

  async function run(list: File[]) {
    if (!list.length || busy) return;
    setError(null);
    try {
      if (list.length > INVOICE_MAX_FILES) throw new Error(`Upload up to ${INVOICE_MAX_FILES} files for one order.`);
      setBusy("Uploading…");
      const files = [];
      for (const raw of list) {
        const f = await prepare(raw);
        const up = await post<{ key: string; url: string; headers: Record<string, string> }>("/api/purchases/files", { name: f.name, contentType: f.type });
        const res = await fetch(up.url, { method: "PUT", headers: up.headers, body: f });
        if (!res.ok) throw new Error(`${f.name} couldn't be uploaded. Try again.`);
        files.push({ key: up.key, name: f.name, contentType: f.type });
      }
      setBusy("Reading the invoice… this takes about 20–40 seconds.");
      onDraft(await post<InvoiceDraft>("/api/purchases/read-invoice", { files }));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
      if (input.current) input.current.value = "";
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div
        role="button"
        tabIndex={0}
        aria-busy={!!busy}
        onClick={() => !busy && input.current?.click()}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), !busy && input.current?.click())}
        onDragOver={(e) => (e.preventDefault(), setOver(true))}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          void run([...e.dataTransfer.files]);
        }}
        onPaste={(e) => {
          const pasted = [...e.clipboardData.files];
          if (pasted.length) (e.preventDefault(), void run(pasted));
        }}
        className={cx(
          "flex cursor-pointer flex-col items-center gap-1 rounded-[var(--radius-control)] border-2 border-dashed px-4 py-4 text-center text-sm outline-none focus-visible:border-brand",
          over ? "border-brand bg-brand-wash" : "border-line bg-mist/50 hover:border-brand/60",
          busy && "cursor-wait opacity-80",
        )}
      >
        {busy ? (
          <span className="flex items-center gap-2 font-medium text-ink">
            <span className="size-4 animate-spin rounded-full border-2 border-brand border-t-transparent" aria-hidden />
            {busy}
          </span>
        ) : (
          <>
            <span className="font-semibold text-ink">Fill from invoice</span>
            <span className="text-ink-soft">Drop, paste, or pick the invoice: a PDF, screenshot, or photo. Several pages of one order are fine.</span>
          </>
        )}
      </div>
      <input
        ref={input}
        type="file"
        hidden
        multiple
        accept="application/pdf,image/*"
        onChange={(e) => void run([...(e.target.files ?? [])])}
      />
      {error && <Alert>{error}</Alert>}
    </div>
  );
}
